/**
 * 소셜 OAuth — Google / Apple authorize URL 로 same-tab redirect (웹 전용).
 *
 * ⚠ 네이티브 (Capacitor Android/iOS) 에서는 본 파일을 거의 쓰지 않는다.
 *   네이티브는 `src/native/socialAuth/{google,apple}.ts` 의 SDK wrapper 가 직접
 *   idToken 을 받아 BE 로 전달한다. LoginPage 가 isNative() 분기.
 *   예외는 Android 의 Apple 로그인 — Apple 은 Android 네이티브 SDK 가 없어
 *   {@link buildAppleNativeAuthorizeUrl} 로 만든 URL 을 Custom Tab 으로 띄운다.
 *
 * 흐름 (웹):
 *   1) generateState() → sessionStorage 저장
 *   2) location.href = authorizeUrl (same-tab)
 *   3) vendor 인증 후 redirectUri 로 redirect — query `?code=&state=` 형태
 *      (Apple 만 form_post 라 BE web-callback 을 한 번 경유해 relay 된다)
 *   4) /oauth/{provider}/callback 페이지가 마운트 → state 검증 → startGoogleAuth/startAppleAuth
 *   5) SocialAuthResult 처리 — 로그인 또는 즉시 가입(가입 화면 없음)
 */
import { env } from "../config/env";

const GOOGLE_AUTHORIZE_URL = "https://accounts.google.com/o/oauth2/v2/auth";
const APPLE_AUTHORIZE_URL = "https://appleid.apple.com/auth/authorize";

const SESSION_KEY_STATE_PREFIX = "template-oauth-state-";
const SESSION_KEY_REDIRECT_PREFIX = "template-oauth-redirect-";

export type CodeFlowProvider = "GOOGLE" | "APPLE";

/**
 * Apple 웹/Android 플로우는 appleid.apple.com → BE web-callback(api.*) → 앱 origin(app.*) 으로
 * cross-site form_post 홉을 거친다. 이 홉을 지나면 일부 브라우저/웹뷰에서 sessionStorage 의
 * CSRF state 가 콜백 시점에 비어버려 "state 검증 실패" 가 난다(Google 은 vendor→앱 직접 GET
 * 복귀라 무사). → sessionStorage 와 별개로 동일 값을 쿠키에도 박아두고, 콜백에서 sessionStorage
 * 우선·쿠키 폴백으로 읽는다. 쿠키는 탭 세션과 무관한 cookie store 에 남아 홉을 견딘다.
 * state 는 무작위 hex 라 민감정보 아님 — host-only(app.*), SameSite=Lax, Secure, 10분 만료.
 */
const STATE_COOKIE_TTL_SEC = 600;

function setOauthCookie(name: string, value: string): void {
  try {
    const secure = location.protocol === "https:" ? "; Secure" : "";
    document.cookie =
      `${name}=${encodeURIComponent(value)}; Max-Age=${STATE_COOKIE_TTL_SEC}; Path=/; SameSite=Lax${secure}`;
  } catch {
    // document.cookie 접근 불가 환경 — sessionStorage 만으로 진행.
  }
}

function readOauthCookie(name: string): string | null {
  try {
    const prefix = `${name}=`;
    const hit = document.cookie.split("; ").find((c) => c.startsWith(prefix));
    return hit ? decodeURIComponent(hit.slice(prefix.length)) : null;
  } catch {
    return null;
  }
}

function clearOauthCookie(name: string): void {
  try {
    document.cookie = `${name}=; Max-Age=0; Path=/; SameSite=Lax`;
  } catch {
    // ignore
  }
}

export function generateState(): string {
  const bytes = new Uint8Array(32);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
}

export function storeState(provider: CodeFlowProvider, state: string): void {
  sessionStorage.setItem(SESSION_KEY_STATE_PREFIX + provider, state);
  setOauthCookie(SESSION_KEY_STATE_PREFIX + provider, state);
}

/** redirectUri 도 state 와 동일하게 sessionStorage + 쿠키에 저장 (cross-site 홉 대비). */
export function storeRedirectUri(provider: CodeFlowProvider, redirectUri: string): void {
  sessionStorage.setItem(SESSION_KEY_REDIRECT_PREFIX + provider, redirectUri);
  setOauthCookie(SESSION_KEY_REDIRECT_PREFIX + provider, redirectUri);
}

/** sessionStorage 우선, 없으면 쿠키 폴백(Apple cross-site 홉 대비). 읽은 뒤 양쪽 모두 제거. */
export function readAndClearState(provider: CodeFlowProvider): string | null {
  const k = SESSION_KEY_STATE_PREFIX + provider;
  const v = sessionStorage.getItem(k) ?? readOauthCookie(k);
  sessionStorage.removeItem(k);
  clearOauthCookie(k);
  return v;
}

export function readAndClearRedirectUri(provider: CodeFlowProvider): string | null {
  const k = SESSION_KEY_REDIRECT_PREFIX + provider;
  const v = sessionStorage.getItem(k) ?? readOauthCookie(k);
  sessionStorage.removeItem(k);
  clearOauthCookie(k);
  return v;
}

interface ProviderConfig {
  authorizeUrl: string;
  clientId: string;
  redirectUri: string;
  scope?: string;
  /** Apple: name/email scope 요청 시 "form_post" 필수 (Apple 이 GET 대신 POST 로 콜백). */
  responseMode?: string;
  extraQuery?: Record<string, string>;
}

function getGoogleConfig(): ProviderConfig {
  const clientId = env.VITE_GOOGLE_CLIENT_ID;
  const redirectUri = env.VITE_GOOGLE_REDIRECT_URI;
  if (!clientId || !redirectUri) {
    throw new Error("Google OAuth 환경변수(VITE_GOOGLE_CLIENT_ID/REDIRECT_URI) 가 설정되지 않았습니다");
  }
  return {
    authorizeUrl: GOOGLE_AUTHORIZE_URL,
    clientId,
    redirectUri,
    scope: "openid email profile",
    // 로그아웃/탈퇴 후 재로그인 시 이전 Google 계정으로 자동 통과되지 않도록 계정 선택 강제.
    //   - prompt="select_account": 단일 세션이어도 계정 선택 화면을 항상 표시 →
    //     사용자가 "예전 계정 자동 로그인" 대신 계정을 다시 고를 수 있다.
    // (재인증 UX 요구로 재도입 — 이전에는 UX 간소화를 위해 제거했었음)
    extraQuery: {
      access_type: "online",
      include_granted_scopes: "true",
      prompt: "select_account",
    },
  };
}

/**
 * Apple — Services ID 로 웹/Android OAuth (form_post).
 *
 * redirect_uri 는 FE 페이지가 아니라 BE web-callback (Apple 이 name/email scope 요청 시
 * GET 이 아닌 application/x-www-form-urlencoded POST 로 콜백하므로 정적 SPA 가 직접 못 받음).
 * BE 가 POST 를 받아 FE `/oauth/apple/callback?code=&state=&name=` 로 302 relay 한다.
 */
function getAppleConfig(): ProviderConfig {
  const clientId = env.VITE_APPLE_CLIENT_ID;
  const redirectUri = env.VITE_APPLE_REDIRECT_URI;
  if (!clientId || !redirectUri) {
    throw new Error("Apple OAuth 환경변수(VITE_APPLE_CLIENT_ID/REDIRECT_URI) 가 설정되지 않았습니다");
  }
  return {
    authorizeUrl: APPLE_AUTHORIZE_URL,
    clientId,
    redirectUri,
    scope: "name email",
    responseMode: "form_post",
  };
}

/**
 * Apple Android 네이티브 — Custom Tab 으로 띄울 때 state 에 붙이는 마커.
 * BE web-callback 이 이 마커를 보고 https 웹 콜백 대신 `peeka://oauth/apple/callback` 딥링크로
 * 302 회신해 외부 브라우저 → 앱 복귀가 되게 한다. hex(0-9a-f)엔 없는 문자라 충돌 없음.
 * ⚠ BE SocialAuthController.appleWebCallback 의 동일 마커와 반드시 일치시킬 것.
 */
export const APPLE_NATIVE_STATE_MARKER = "-app";

function getProviderConfig(provider: CodeFlowProvider): ProviderConfig {
  return provider === "APPLE" ? getAppleConfig() : getGoogleConfig();
}

/**
 * provider authorize URL 을 만들고 state/redirectUri 를 저장(sessionStorage+쿠키)한다.
 * @param nativeAppleHop true 면 Apple state 에 native 마커를 붙인다(BE 가 peeka:// 딥링크로 복귀).
 */
function buildAuthorizeUrl(provider: CodeFlowProvider, nativeAppleHop = false): string {
  const cfg = getProviderConfig(provider);
  const state = nativeAppleHop ? generateState() + APPLE_NATIVE_STATE_MARKER : generateState();
  storeState(provider, state);
  storeRedirectUri(provider, cfg.redirectUri);

  const params = new URLSearchParams({
    response_type: "code",
    client_id: cfg.clientId,
    redirect_uri: cfg.redirectUri,
    state,
    ...(cfg.scope ? { scope: cfg.scope } : {}),
    ...(cfg.responseMode ? { response_mode: cfg.responseMode } : {}),
    ...(cfg.extraQuery ?? {}),
  });
  return `${cfg.authorizeUrl}?${params.toString()}`;
}

/**
 * 지정 provider 의 authorize URL 로 same-tab redirect (웹 전용).
 * @returns 호출 즉시 location.href 변경 — 함수가 resolve 되지 않음 (페이지 떠남).
 */
function startCodeFlow(provider: CodeFlowProvider): void {
  window.location.href = buildAuthorizeUrl(provider);
}

/**
 * Apple Android 네이티브 Custom Tab 용 authorize URL.
 * state 저장(sessionStorage+쿠키) + native 마커 포함 — native/socialAuth/apple.ts 가 Browser.open 한다.
 */
export function buildAppleNativeAuthorizeUrl(): string {
  return buildAuthorizeUrl("APPLE", true);
}

export const startGoogleFlow = () => startCodeFlow("GOOGLE");
export const startAppleFlow = () => startCodeFlow("APPLE");
